import styled, { css } from 'styled-components';
import { AlignCenter, FlexBetween, FlexCenter, LineClamp } from '../../../../../global/commonStyle';
import { mobile_xl } from '../../../../../global/mediaQueryMixin';
import { Typography } from '../../../../../global/theme';

interface Props {
  isOverdue?: boolean;
  hasContent?: boolean;
  isShowCompletedTask?: boolean;
  isShowAllCompletedTask?: boolean;
  hasGap?: boolean;
  hasLeftBorder?: boolean;
  isEmptyState?: boolean;
  color?: string;
}

const IconHidden = styled.div`
  position: absolute;
  left: -15px;
  top: 9px;
  svg {
    opacity: 0;
    path {
      fill: var(--text-secondary);
    }
  }
  ${mobile_xl(css`
    display: none;
  `)}
`;

const TaskContent = styled.div`
  position: relative;
  ${FlexBetween}
  border: 0.5px solid transparent;
  padding: var(--space-4) var(--space-6);
  margin: var(--space-0) -1px;
  border-radius: var(--border-radius-full);
  gap: var(--space-40);
  &:hover {
    background-color: var(--background-primary);
    border: 0.5px solid var(--border-primary);
    box-shadow: var(--shadow-card);

    ${IconHidden} {
      svg {
        opacity: 1;
      }
    }
    .plus-icon {
      visibility: visible;
      margin-left: -9px;
    }
    .task-input {
      background-color: var(--background-primary);
    }
    .calendar-icon {
      visibility: visible;
    }
  }
  ${mobile_xl(css`
    padding: var(--space-8) var(--space-0);
    margin: var(--space-0);
    border: 0;
    &:hover {
      background-color: unset;
      border: none;
      box-shadow: none;
    }
  `)}
`;

const TaskItem = styled.div`
  ${AlignCenter}
  gap: var(--space-16);
  ${mobile_xl(css`
    width: 100%;
    justify-content: space-between;
    gap: var(--space-8);
  `)}
`;

const Form = styled.div`
  ${AlignCenter}
  gap: var(--space-6);
  ${mobile_xl(css`
    gap: var(--space-10);
  `)}
`;

const Label = styled.label`
  ${Typography.body_md}
  color: var(--text-primary);
  margin: var(--space-0);
  ${LineClamp(1)}
  word-break: break-all;
`;

const AssignGroup = styled.div`
  ul {
    ${AlignCenter}
    flex-wrap: wrap;
    list-style-type: none;
    padding: var(--space-0);
    margin: var(--space-0);
    width: max-content;
  }

  li {
    margin-left: -9px;
    border-radius: var(--border-radius-full);
    background-color: var(--background-muted);
    z-index: var(--z-index-base);
    width: 20px;
    height: 20px;
    ${FlexCenter}
    border: 1px solid var(--background-secondary);
  }
  .plus-icon {
    visibility: hidden;
  }
  ${mobile_xl(css`
    .plus-icon {
      display: none;
      visibility: hidden;
    }
  `)}
`;

const StatusDropdown = styled.div`
  display: flex;
  gap: var(--space-2);
`;

const ButtonIcon = styled.div`
  padding: var(--space-4);
  border-radius: var(--border-radius-12);
  display: flex;
  &:hover {
    background-color: var(--neutral-secondary);
    input {
      filter: brightness(var(--brightness-value));
    }
  }
`;

const Deadline = styled.div`
  ${AlignCenter}
  gap: var(--space-12);
  ${(props: Props) =>
    props.hasGap &&
    css`
      gap: var(--space-0);
    `}
  .calendar-icon {
    visibility: hidden;
  }
  svg {
    vertical-align: middle;
  }
  ${mobile_xl(css`
    display: none;
  `)}
`;

const DeadBlock = styled.div<Props>`
  ${AlignCenter}
  gap: var(--space-4);
  ${(props) =>
    props.hasLeftBorder &&
    css`
      padding-left: var(--space-12);
      border-left: 0.5px solid var(--border-primary);
    `}
  span {
    ${AlignCenter}
    gap: var(--space-4);
  }
`;

const Text = styled.p<Props>`
  ${Typography.body_md}
  color: var(--text-secondary);
  margin: var(--space-0);
  span {
    color: var(--accent-error);
  }
  ${(props) =>
    props.isOverdue &&
    css`
      color: var(--accent-error);
    `}
`;

const IconDueDate = styled.div`
  .calendar-fill-color {
    fill: var(--text-secondary);
  }
  .calendar-due-fill-color {
    fill: var(--accent-error);
  }
`;

const MultipleLabel = styled.div`
  border-radius: var(--border-radius-full);
  border: 0.5px solid var(--border-primary);
  display: flex;
  > div {
    &:not(:first-child) {
      border-left: 0.5px solid var(--border-primary);
    }
  }
`;

const DropBlock = styled.div`
  ${AlignCenter}
  gap: var(--space-4);
  padding: var(--space-2) var(--space-10) var(--space-2) var(--space-6);
`;

const DotIcon = styled.div<Props>`
  margin: var(--space-4);
  width: 8px;
  height: 8px;
  border-radius: var(--border-radius-full);
  background-color: ${({ color }) => color};
`;

const DropLabel = styled.div`
  ${Typography.body_md}
  color: var(--text-primary);
  white-space: nowrap;
`;

const TooltipContent = styled.div`
  ${AlignCenter}
  gap: var(--space-4);
`;

export {
  TaskContent,
  TaskItem,
  Form,
  Label,
  AssignGroup,
  StatusDropdown,
  ButtonIcon,
  IconHidden,
  Deadline,
  DeadBlock,
  Text,
  IconDueDate,
  MultipleLabel,
  DropBlock,
  DotIcon,
  DropLabel,
  TooltipContent
};
